import AgnVotesEditor from 'components/Modal/AgnVotesEditor';
import { EventType } from 'components/Modal/AgnVotesEditor/AgnVotesEditor';
import { clsx, isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import moment from 'moment';
import {
  Col,
  Collapse,
  EditIcon,
  IconButton,
  Row,
} from 'plataforma-fundacao-componentes';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AgendaProps, VoteOption } from 'services/editVotes';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { IReducer } from 'store/Reducer';
import styles from '../editarVotos.module.scss';

const calculatePercentage = (votes: number, totalVotes: number): number => {
  if (totalVotes === 0) {
    return 0;
  }
  return (votes / totalVotes) * 100;
};

const VoteItem = React.memo(
  ({ description, votes, votesTotal }: VoteOption) => (
    <Col className={styles.options}>
      <div className={styles.title}>
        <p>{description}</p>
        <p>
          {votes} / {votesTotal}
        </p>
      </div>
      <div className={styles.progress}>
        <div
          className={styles.percent}
          style={{ width: `${calculatePercentage(votes, votesTotal)}%` }}
        />
      </div>
    </Col>
  )
);

const Staves = ({
  agendas,
  type,
}: {
  agendas: AgendaProps[];
  type: EventType;
}) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { usuario } = useSelector((state: IReducer) => state.auth);

  const [openedIndices, setOpenedIndices] = useState<{
    [key: number]: boolean;
  }>({});

  const toggleCollapse = useCallback((index: number) => {
    setOpenedIndices((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const singleVote = useMemo(() => {
    return [EventType.AGE, EventType.AGO, EventType.AOE].includes(type);
  }, [type]);

  const handleEditModal = useCallback(
    (
      agendaId: number,
      eventId: number,
      description: string,
      branch: string,
      nucleou: string,
      ldap: string,
      type: EventType,
      associateElegible: number,
      associatePresent: number,
      options: Omit<VoteOption, 'totalVotes' | 'votesCount' | 'options'>[]
    ) => {
      const modalKey: string = getUniqueKey();

      return dispatch(
        openModal(AgnVotesEditor, {
          agendaId,
          eventId,
          description: description,
          branch: branch,
          nucleou: nucleou,
          ldap,
          type,
          associateElegible: associateElegible,
          associatePresent: associatePresent,
          options: options,
          modalKey: modalKey,
          preventEscExit: true,
          preventMaskExit: false,
          mobileOnXS: false,
          onClose: () => closeModal(modalKey),
          onSuccess: () => closeModal(modalKey),
        })
      );
    },
    [dispatch]
  );

  return (
    <Row className={styles.staves}>
      {agendas?.map((infos, index) => {
        const isOpened = openedIndices[index] ?? false;
        return (
          <Row className={styles['staves-body']} key={index}>
            <Col
              style={{
                width: '100%',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <h1>{infos.title ?? '-'}</h1>
              {infos.editUser ? (
                <h1>
                  Editado por:{' '}
                  <b className='nunito'>
                    {infos.editUser} -&nbsp;
                    {moment(infos.editDate).format('DD/MM/YYYY - HH:mm:ss')}
                  </b>
                </h1>
              ) : (
                <Fragment />
              )}
            </Col>
            <Col>
              <h3>
                {translation.Assessor.Evento.Vote.agendas.associate(
                  infos.present,
                  infos.ablePresent
                )}
              </h3>
            </Col>
            <Row>
              {infos.items.map((x) => (
                <VoteItem key={x.itemId} {...x} />
              ))}
            </Row>
            <Row
              className={clsx(
                styles['outer-votes'],
                !infos.details.length && styles.disabled
              )}
            >
              <Col noPadding className={styles['header-votes']}>
                <button
                  className={styles.disabled}
                  onClick={() => toggleCollapse(index)}
                  disabled={!infos.details.length}
                  tabIndex={!infos.details.length ? -1 : 0}
                >
                  {translation.Assessor.Evento.Vote.agendas.button}
                </button>
              </Col>
              <Collapse opened={isOpened} animateOpacity>
                <Row className={styles['collapse-votes']}>
                  {infos.details.map((details) => (
                    <Row
                      className={styles['agencies-votes']}
                      key={`${details.branch}-${details.nucleus}`}
                    >
                      <Row>
                        <Col className={styles['title-container']}>
                          <h3>
                            {translation.Assessor.Evento.Vote.agendas.infos(
                              details.branch,
                              details.nucleus
                            )}
                          </h3>
                          <IconButton
                            icon={<EditIcon />}
                            onClick={() =>
                              handleEditModal(
                                infos.agendaId,
                                Number(id),
                                infos.title,
                                details.branch,
                                details.nucleus,
                                usuario?.ldap!,
                                type as EventType,
                                details.ablePresent,
                                details.present,
                                details.item
                              )
                            }
                            disabled={
                              isNullOrUndefined(details.ablePresent) ||
                              (!singleVote && details.ablePresent === 0)
                            }
                          />
                        </Col>
                        {details.item.map((option: VoteOption) => (
                          <VoteItem key={option.itemId} {...option} />
                        ))}
                      </Row>
                    </Row>
                  ))}
                </Row>
              </Collapse>
            </Row>
          </Row>
        );
      })}
    </Row>
  );
};

export default React.memo(Staves);
